import { memo, useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { CloseIcon } from '@library';
import type { ProductVehicle } from '../../types';
import HorizontalLayout from '../../../core/components/layout/HorizontalLayout';
import VerticalLayout from '../../../core/components/layout/VerticalLayout';
import filterVehicles from '../../util/filterVehicles';
import getVehicleFilterValues from '../../util/getVehicleFilterValues';
import motorcycleSorter from '../../util/motorcycleSorter';
import MultiSelect from '../../../core/components/input/Select/MultiSelect';
import useStoreAndLanguageParams from '../../../core/hooks/useStoreAndLanguageParams';
import ProductCompatibilityTable from './ProductTables/ProductCompatibilityTable';
import styles from './ProductCompatibility.module.css';

interface ProductCompatibilityFilters {
    make: string[];
    model: string[];
    ccm: string[];
    ratedPower: string[];
    modelYear: string[];
    type: string[];
    typeApprovalList: string[];
}

const filtersInitialValue = {
    make: [],
    model: [],
    ccm: [],
    ratedPower: [],
    modelYear: [],
    type: [],
    typeApprovalList: [],
};

const ProductCompatibility = ({ vehicles }: { vehicles: ProductVehicle[] }) => {
    const { storeRootPath } = useStoreAndLanguageParams();

    const intl = useIntl();
    const [filters, setFilters] =
        useState<ProductCompatibilityFilters>(filtersInitialValue);

    const resetFilters = () => {
        setFilters(filtersInitialValue);
    };

    const [areFiltersActive, filteredVehicles] = filterVehicles(
        vehicles,
        filters
    );

    const validFilterValues = getVehicleFilterValues(vehicles, filters);
    const processedValidFilterValues = Object.entries(validFilterValues)
        .slice()
        .reverse();

    const handleModelChange = (vehicleId: string) => {
        window.open(
            `${storeRootPath}/vehicle-search?vehicleId=${vehicleId}`,
            '_blank'
        );
    };

    return (
        <div data-test-class="productCompatibilityTableCollapsibleTrigger">
            <VerticalLayout reversed gap="medium" stretchChildren>
                <ProductCompatibilityTable
                    vehicles={filteredVehicles.slice().sort(motorcycleSorter)}
                    onModelChange={handleModelChange}
                />
                <VerticalLayout
                    gap={areFiltersActive ? 'small' : 'large'}
                    stretchChildren
                >
                    <div className={styles.compatibilityFilters}>
                        {processedValidFilterValues.map(
                            ([key, values], index) => (
                                <div
                                    key={key}
                                    className={styles.compatibilityFilter}
                                    style={{ order: -index }}
                                >
                                    <MultiSelect
                                        fullWidth
                                        keepLabel
                                        bordered
                                        menuSize="full"
                                        data-test-class="productCompatibilityTableFilter"
                                        data-test-state={key}
                                        placeholder={intl.formatMessage({
                                            id:
                                                key === 'type'
                                                    ? 'type-nr'
                                                    : key,
                                        })}
                                        disabled={values.length === 0}
                                        options={values.map((item: string) => ({
                                            label: item.toUpperCase(),
                                            value: `${item}`,
                                        }))}
                                        value={
                                            filters[
                                                key as keyof ProductCompatibilityFilters
                                            ]
                                        }
                                        onChange={value =>
                                            setFilters(currentFilters => ({
                                                ...currentFilters,
                                                [key]: value,
                                            }))
                                        }
                                    />
                                </div>
                            )
                        )}
                    </div>
                    {areFiltersActive && (
                        <div
                            role="button"
                            tabIndex={0}
                            onClick={resetFilters}
                            onKeyPress={(e: React.KeyboardEvent) => {
                                if (e.key === ' ' || e.key === 'Enter') {
                                    resetFilters();
                                }
                            }}
                            className={styles.clearFiltersButton}
                        >
                            <HorizontalLayout gap="small" align="center">
                                <CloseIcon />
                                <span>
                                    <FormattedMessage id="reset filter" />
                                </span>
                            </HorizontalLayout>
                        </div>
                    )}
                </VerticalLayout>
            </VerticalLayout>
        </div>
    );
};

export default memo(ProductCompatibility);
